"use client"

import * as React from "react"
import { Header } from "@/components/header"
import { Footer } from "@/components/footer"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { 
  Calendar, 
  Clock, 
  MapPin, 
  Monitor, 
  Users, 
  Search,
  ChevronLeft,
  ChevronRight,
  Trophy,
  Gamepad2,
  PartyPopper,
  Flag
} from "lucide-react"
import { events, eventTypes, type EventType } from "@/lib/events-data"

const typeConfig = {
  tournament: { label: "Tournament", color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", icon: Trophy },
  community: { label: "Community", color: "bg-primary/20 text-primary border-primary/30", icon: Gamepad2 },
  meetup: { label: "Meetup", color: "bg-accent/20 text-accent border-accent/30", icon: PartyPopper },
  season: { label: "Season", color: "bg-green-500/20 text-green-400 border-green-500/30", icon: Flag }
}

export default function EventsPage() {
  const [activeType, setActiveType] = React.useState<EventType>("All")
  const [searchQuery, setSearchQuery] = React.useState("")
  const [viewMode, setViewMode] = React.useState<"list" | "calendar">("list")
  const [currentMonth, setCurrentMonth] = React.useState(new Date(2026, 4)) // May 2026

  const filteredEvents = events.filter(event => {
    const matchesType = activeType === "All" || event.type === activeType
    const matchesSearch = event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         event.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (event.game?.toLowerCase().includes(searchQuery.toLowerCase()))
    return matchesType && matchesSearch
  }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

  // Calendar helpers
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    return { firstDay, daysInMonth }
  }

  const { firstDay, daysInMonth } = getDaysInMonth(currentMonth)

  const getEventsForDay = (day: number) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    return events.filter(e => e.date === dateStr)
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Hero */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10" />
          <div className="relative mx-auto max-w-6xl px-6 lg:px-8 py-12 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">Events</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Tournaments, community nights, and IRL meetups. Find your next gaming event.
            </p>
          </div>
        </div>

        <div className="mx-auto max-w-6xl px-6 lg:px-8">
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search events..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-card border-border"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {eventTypes.map(type => (
                <Button
                  key={type}
                  variant={activeType === type ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActiveType(type)}
                  className={activeType === type ? "bg-primary" : ""}
                >
                  {type === "All" ? "All Events" : typeConfig[type].label}
                </Button>
              ))}
            </div>
          </div>

          {/* View Toggle */}
          <div className="flex justify-between items-center mb-6">
            <p className="text-sm text-muted-foreground">
              {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''} found
            </p>
            <div className="flex gap-2">
              <Button
                variant={viewMode === "list" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("list")}
              >
                List
              </Button>
              <Button
                variant={viewMode === "calendar" ? "default" : "outline"}
                size="sm"
                onClick={() => setViewMode("calendar")}
              >
                Calendar
              </Button>
            </div>
          </div>

          {viewMode === "list" ? (
            /* List View */
            <div className="space-y-4">
              {filteredEvents.map(event => {
                const config = typeConfig[event.type]
                const Icon = config.icon
                const eventDate = new Date(event.date)
                
                return (
                  <Card key={event.id} className="bg-card border-border hover:border-primary/50 transition-colors">
                    <CardContent className="p-0">
                      <div className="flex flex-col md:flex-row">
                        {/* Date Column */}
                        <div className="flex md:flex-col items-center justify-center gap-2 p-4 md:p-6 bg-secondary/50 md:w-28 text-center">
                          <span className="text-sm text-muted-foreground uppercase">
                            {eventDate.toLocaleDateString('en-US', { month: 'short' })}
                          </span>
                          <span className="text-3xl font-bold text-foreground">
                            {eventDate.getDate()}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {eventDate.toLocaleDateString('en-US', { weekday: 'short' })}
                          </span>
                        </div>

                        {/* Content */}
                        <div className="flex-1 p-4 md:p-6">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <Badge className={config.color}>
                              <Icon className="h-3 w-3 mr-1" />
                              {config.label}
                            </Badge>
                            {event.game && (
                              <Badge variant="outline">{event.game}</Badge>
                            )}
                            {event.region && (
                              <Badge variant="outline">{event.region}</Badge>
                            )}
                          </div>

                          <h3 className="text-lg font-semibold text-foreground mb-2">{event.title}</h3>
                          <p className="text-sm text-muted-foreground mb-4">{event.description}</p>

                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {event.time}
                            </span>
                            <span className="flex items-center gap-1">
                              {event.online ? (
                                <>
                                  <Monitor className="h-4 w-4" />
                                  Online
                                </>
                              ) : (
                                <>
                                  <MapPin className="h-4 w-4" />
                                  In Person
                                </>
                              )}
                            </span>
                            {event.spotsTotal && (
                              <span className="flex items-center gap-1">
                                <Users className="h-4 w-4" />
                                {event.spotsTaken}/{event.spotsTotal} spots filled
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Action */}
                        <div className="flex items-center p-4 md:p-6 border-t md:border-t-0 md:border-l border-border">
                          <Button className="w-full md:w-auto bg-primary hover:bg-primary/90">
                            {event.type === "season" ? "Learn More" : "Register"}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}

              {filteredEvents.length === 0 && (
                <div className="text-center py-12">
                  <Calendar className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No events found matching your filters.</p>
                </div>
              )}
            </div>
          ) : (
            /* Calendar View */
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                {/* Calendar Header */}
                <div className="flex items-center justify-between mb-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <h2 className="text-lg font-semibold text-foreground">
                    {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1">
                  {/* Day Headers */}
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
                      {day}
                    </div>
                  ))}

                  {/* Empty cells for days before month starts */}
                  {Array.from({ length: firstDay }).map((_, i) => (
                    <div key={`empty-${i}`} className="p-2 min-h-24" />
                  ))}

                  {/* Day cells */}
                  {Array.from({ length: daysInMonth }).map((_, i) => {
                    const day = i + 1
                    const dayEvents = getEventsForDay(day)
                    
                    return (
                      <div
                        key={day}
                        className={`p-2 min-h-24 border border-border rounded-lg ${
                          dayEvents.length > 0 ? 'bg-secondary/30' : ''
                        }`}
                      >
                        <span className="text-sm font-medium text-foreground">{day}</span>
                        <div className="mt-1 space-y-1">
                          {dayEvents.slice(0, 2).map(event => (
                            <div
                              key={event.id}
                              className={`text-xs p-1 rounded truncate ${typeConfig[event.type].color}`}
                            >
                              {event.title}
                            </div>
                          ))}
                          {dayEvents.length > 2 && (
                            <div className="text-xs text-muted-foreground">
                              +{dayEvents.length - 2} more
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      <Footer />
    </div>
  )
}
